/**
 * Service for managing authentication operations with the ConnectPath API.
 * 
 * This service provides real API implementations for authentication functionality
 * including login, logout, registration, and token management. It communicates
 * with the backend authentication endpoints to manage user sessions.
 * 
 * This service provides methods for:
 * - User authentication via API (login/logout)
 * - User registration through API
 * - Token management and refresh
 * - User profile retrieval from API
 * - Password management operations
 * 
 * @example
 * const authService = new ApiAuthService('/api/v1');
 * const tokenResponse = await authService.login({ email: 'admin@connectpath.com', password: 'admin123' });
 */
import { BaseService } from '../base/BaseService';
import { IAuthService } from '../../interfaces/IAuthService';
import { User, LoginCredentials, RegisterData, TokenResponse } from '../../../types/user';
import { config } from '../../../config/environment';
import { enhancedTokenManager as TokenManager } from '../../EnhancedTokenManager';

export class ApiAuthService extends BaseService implements IAuthService {
  private currentUser: User | null = null;

  constructor(private apiUrl: string) {
    super('ApiAuthService');
  }

  /**
   * Authenticate a user with the API using email and password.
   * 
   * @param {LoginCredentials} credentials - The user's login credentials
   * @returns {Promise<TokenResponse>} Token response with access token and user data
   * @throws {Error} If authentication fails or network error occurs
   */
  async login(credentials: LoginCredentials): Promise<TokenResponse> {
    const response = await fetch(
      `/api/v1/auth/login/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        })
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const data = await response.json();
    
    // Store tokens using TokenManager
    const tokenManager = TokenManager.getInstance();
    tokenManager.initialize(data.access_token, data.expires_at);
    
    // Transform user data to match frontend expectations
    const transformedUser = {
      ...data.user,
      churchId: data.user.church_id, // Transform snake_case to camelCase
      // Keep church_id for backward compatibility
    };
    
    // Store transformed user data
    localStorage.setItem('connectpath_user', JSON.stringify(transformedUser));
    this.currentUser = transformedUser;

    // Return in the format expected by AuthContext
    return {
      access_token: data.access_token,
      expires_at: data.expires_at,
      user: transformedUser
    };
  }

  async logout(): Promise<void> {
    const token = this.getAuthToken();
    
    if (token) {
      try {
        await fetch(
          `/api/v1/auth/logout/`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    // Clear local data using TokenManager
    const tokenManager = TokenManager.getInstance();
    tokenManager.clearToken();
    
    this.currentUser = null;
    localStorage.removeItem('connectpath_user');
    this.clearCache();
  }

  async register(data: RegisterData): Promise<User> {
    const response = await fetch(
      `/api/v1/auth/register/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }

    return response.json();
  }

  async getCurrentUser(): Promise<User | null> {
    // Try to get from memory first
    if (this.currentUser) {
      return this.currentUser;
    }

    // Try to restore from localStorage
    const storedUser = localStorage.getItem('connectpath_user');
    if (storedUser) {
      this.currentUser = JSON.parse(storedUser);
      return this.currentUser;
    }

    // If we have a token but no user data, fetch from API
    const token = this.getAuthToken();
    if (token) {
      try {
        const response = await fetch(
          `/api/v1/auth/me/`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const userData = await response.json();
          // Transform user data to match frontend expectations
          const transformedUser = {
            ...userData,
            churchId: userData.church_id, // Transform snake_case to camelCase
            // Keep church_id for backward compatibility
          };
          this.currentUser = transformedUser;
          localStorage.setItem('connectpath_user', JSON.stringify(transformedUser));
          return transformedUser;
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    }

    return null;
  }

  async updateProfile(updates: Partial<User>): Promise<User> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `/api/v1/auth/profile`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Profile update failed');
    }

    const updatedUserData = await response.json();
    // Transform user data to match frontend expectations
    const transformedUser = {
      ...updatedUserData,
      churchId: updatedUserData.church_id, // Transform snake_case to camelCase
      // Keep church_id for backward compatibility
    };
    this.currentUser = transformedUser;
    localStorage.setItem('connectpath_user', JSON.stringify(transformedUser));

    return transformedUser;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `/api/v1/auth/change-password`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Password change failed');
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    const response = await fetch(
      `/api/v1/auth/request-password-reset`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Password reset request failed');
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await fetch(
      `/api/v1/auth/reset-password`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          newPassword
        })
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Password reset failed');
    }
  }

  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    const response = await fetch(
      `/api/v1/auth/refresh/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh: refreshToken })
      }
    );

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    
    // Update stored tokens using TokenManager
    const tokenManager = TokenManager.getInstance();
    tokenManager.initialize(data.access_token || data.access, data.expires_at);
    
    return {
      access: data.access_token || data.access,
      refresh: data.refresh || refreshToken,
      user: this.currentUser!
    };
  }

  async validateToken(token: string): Promise<boolean> {
    try {
      const response = await fetch(
        `/api/v1/auth/validate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        }
      );

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  getAuthToken(): string | null {
    const tokenManager = TokenManager.getInstance();
    return tokenManager.getToken();
  }

  /**
   * Get user preferences for theme, notifications, and accessibility.
   *
   * @returns {Promise<any>} User preferences object
   * @throws {Error} If fetching preferences fails
   */
  async getPreferences(): Promise<any> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(
      `/api/v1/auth/users/preferences/`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch preferences');
    }

    return response.json();
  }

  /**
   * Update user preferences for theme, notifications, and accessibility.
   *
   * @param {any} preferences - The preferences object to update
   * @param {boolean} partial - Whether to do a partial update (PATCH) or full update (PUT)
   * @returns {Promise<any>} Updated preferences object
   * @throws {Error} If updating preferences fails
   */
  async updatePreferences(preferences: any, partial: boolean = true): Promise<any> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(
      `/api/v1/auth/users/preferences/`,
      {
        method: partial ? 'PATCH' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update preferences');
    }

    return response.json();
  }
}